/* eslint-disable react-refresh/only-export-components */
import { useGetList } from "ra-core";
import { useMemo, useState } from "react";
import {
  AreaChart,
  BarChart3,
  BarChartHorizontal,
  Filter,
  Hash,
  LineChart,
  PieChart,
  Plus,
  Table2,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { ChartType, Contact, Deal } from "../types";
import { ReportWidget, effectiveChartType } from "./ReportWidget";
import { useReportFieldDefs, type ReportFieldDef } from "./useReportFields";

/**
 * Custom report builder (Reports v3) - HubSpot-style assembly: a stepped
 * configuration rail on the left (data → measure → group → filters → chart
 * type gallery) with an always-live preview on the right. Aggregation runs
 * client-side over the dataProvider at pilot scale; the same ReportConfig
 * shape moves server-side with Phase 7.
 */

// ---------------------------------------------------------------------------
// Config model
// ---------------------------------------------------------------------------

type FieldType = ReportFieldDef["type"];
type FieldDef = ReportFieldDef;

export type { ChartType };

export interface FilterRow {
  id: number;
  field: string;
  op: string;
  value: string;
  value2?: string;
}

export interface ReportConfig {
  name?: string;
  object: "deals" | "contacts";
  measure: "count" | "sum_amount" | "avg_amount";
  groupBy: string;
  logic: "all" | "any";
  filters: FilterRow[];
  /** Unset = auto (stat when ungrouped, column for dates, bar otherwise). */
  chartType?: ChartType;
}

const OPS: Record<FieldType, { id: string; name: string }[]> = {
  number: [
    { id: "eq", name: "=" },
    { id: "neq", name: "≠" },
    { id: "gt", name: ">" },
    { id: "gte", name: "≥" },
    { id: "lt", name: "<" },
    { id: "lte", name: "≤" },
  ],
  text: [
    { id: "contains", name: "contains" },
    { id: "eq", name: "equals" },
  ],
  enum: [
    { id: "is", name: "is" },
    { id: "is_not", name: "is not" },
  ],
  owner: [
    { id: "is", name: "is" },
    { id: "is_not", name: "is not" },
  ],
  date: [
    { id: "last_days", name: "in last N days" },
    { id: "before", name: "before" },
    { id: "after", name: "after" },
    { id: "between", name: "between" },
  ],
};

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

const valueOf = (record: any, field: FieldDef) =>
  field.getValue ? field.getValue(record) : record?.[field.key];

const matches = (record: any, f: FilterRow, field: FieldDef): boolean => {
  const v = valueOf(record, field);
  switch (field.type) {
    case "number": {
      const n = Number(v);
      const target = Number(f.value);
      if (Number.isNaN(n) || Number.isNaN(target)) return false;
      switch (f.op) {
        case "eq": return n === target;
        case "neq": return n !== target;
        case "gt": return n > target;
        case "gte": return n >= target;
        case "lt": return n < target;
        case "lte": return n <= target;
      }
      return false;
    }
    case "text": {
      const s = String(v ?? "").toLowerCase();
      const target = f.value.toLowerCase();
      return f.op === "contains" ? s.includes(target) : s === target;
    }
    case "enum":
    case "owner": {
      const s = String(v ?? "");
      return f.op === "is" ? s === f.value : s !== f.value;
    }
    case "date": {
      if (!v) return false;
      const t = new Date(v).getTime();
      if (Number.isNaN(t)) return false;
      switch (f.op) {
        case "last_days": {
          const days = Number(f.value);
          if (Number.isNaN(days)) return false;
          return t >= Date.now() - days * 86400_000;
        }
        case "before": return f.value ? t < new Date(f.value).getTime() : false;
        case "after": return f.value ? t > new Date(f.value).getTime() : false;
        case "between": {
          if (!f.value || !f.value2) return false;
          return (
            t >= new Date(f.value).getTime() &&
            t <= new Date(f.value2).getTime() + 86400_000
          );
        }
      }
      return false;
    }
  }
};

export const runReport = (
  records: any[],
  config: ReportConfig,
  fields: FieldDef[],
) => {
  const fieldOf = (key: string) => fields.find((f) => f.key === key);
  const active = config.filters.filter((f) => fieldOf(f.field) && f.value !== "");
  const rows = records.filter((r) => {
    if (active.length === 0) return true;
    const results = active.map((f) => matches(r, f, fieldOf(f.field)!));
    return config.logic === "all" ? results.every(Boolean) : results.some(Boolean);
  });

  const measureOf = (group: any[]) => {
    switch (config.measure) {
      case "count": return group.length;
      case "sum_amount": return group.reduce((s, d) => s + (Number(d.amount) || 0), 0);
      case "avg_amount":
        return group.length === 0
          ? 0
          : Math.round(group.reduce((s, d) => s + (Number(d.amount) || 0), 0) / group.length);
    }
  };

  const groupField = fieldOf(config.groupBy);
  if (!groupField) return { rows, groups: null, total: measureOf(rows) };

  const buckets = new Map<string, any[]>();
  const labelFor = (r: any): string => {
    const v = valueOf(r, groupField);
    if (groupField.type === "date") {
      if (!v) return "(none)";
      return String(v).slice(0, 7); // YYYY-MM
    }
    if (groupField.choices) {
      return groupField.choices.find((c) => c.id === String(v))?.name ?? "(none)";
    }
    return v == null || v === "" ? "(none)" : String(v);
  };
  for (const r of rows) {
    const label = labelFor(r);
    buckets.set(label, [...(buckets.get(label) ?? []), r]);
  }
  let groups = [...buckets.entries()].map(([label, group]) => ({
    label,
    value: measureOf(group),
    count: group.length,
  }));
  groups =
    groupField.type === "date"
      ? groups.sort((a, b) => a.label.localeCompare(b.label))
      : groups.sort((a, b) => b.value - a.value);
  return { rows, groups, total: measureOf(rows) };
};

// ---------------------------------------------------------------------------
// Chart type gallery
// ---------------------------------------------------------------------------

export const CHART_TYPES: {
  id: ChartType;
  label: string;
  icon: typeof Hash;
  needsGroup: boolean;
}[] = [
  { id: "stat", label: "KPI", icon: Hash, needsGroup: false },
  { id: "column", label: "Column", icon: BarChart3, needsGroup: true },
  { id: "bar", label: "Bar", icon: BarChartHorizontal, needsGroup: true },
  { id: "line", label: "Line", icon: LineChart, needsGroup: true },
  { id: "area", label: "Area", icon: AreaChart, needsGroup: true },
  { id: "donut", label: "Donut", icon: PieChart, needsGroup: true },
  { id: "funnel", label: "Funnel", icon: Filter, needsGroup: true },
  { id: "table", label: "Table", icon: Table2, needsGroup: true },
];

const ChartTypeGallery = ({
  active,
  hasGroup,
  onPick,
}: {
  active: ChartType;
  hasGroup: boolean;
  onPick: (t: ChartType) => void;
}) => (
  <div className="grid grid-cols-4 gap-1.5">
    {CHART_TYPES.map(({ id, label, icon: Icon, needsGroup }) => {
      const disabled = needsGroup && !hasGroup;
      return (
        <button
          key={id}
          type="button"
          disabled={disabled}
          title={disabled ? "Pick a “group by” first" : label}
          onClick={() => onPick(id)}
          className={`flex flex-col items-center gap-1 rounded-md border px-1 py-2 text-[11px] transition-colors ${
            active === id
              ? "border-primary bg-primary/10 text-foreground"
              : disabled
                ? "border-border text-muted-foreground/40 cursor-not-allowed"
                : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
          }`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

let nextId = 1;

const StepLabel = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-muted text-[10px] font-semibold text-foreground/70">
      {n}
    </span>
    {children}
  </p>
);

export const ReportBuilder = () => {
  const { currency } = useConfigurationContext();
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });

  const [config, setConfig] = useState<ReportConfig>({
    object: "deals",
    measure: "sum_amount",
    groupBy: "stage",
    logic: "all",
    filters: [],
  });

  const fields = useReportFieldDefs(config.object);
  const records = config.object === "deals" ? deals : contacts;
  const result = useMemo(
    () => runReport(records, config, fields),
    [records, config, fields],
  );

  const fmt = (n: number) =>
    config.measure === "count"
      ? n.toLocaleString("en-US")
      : n.toLocaleString("en-US", {
          notation: "compact",
          style: "currency",
          currency,
          currencyDisplay: "narrowSymbol",
          maximumFractionDigits: 1,
        });

  const groupableFields = fields.filter(
    (f) => f.type === "enum" || f.type === "owner" || f.type === "date",
  );
  const isTimeSeries =
    fields.find((f) => f.key === config.groupBy)?.type === "date";
  const activeChart = effectiveChartType(config, isTimeSeries);

  const update = (patch: Partial<ReportConfig>) =>
    setConfig((c) => ({ ...c, ...patch }));
  const updateFilter = (id: number, patch: Partial<FilterRow>) =>
    setConfig((c) => ({
      ...c,
      filters: c.filters.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));

  const addFilter = () => {
    const first = fields[0]!;
    update({
      filters: [
        ...config.filters,
        { id: nextId++, field: first.key, op: OPS[first.type][0]!.id, value: "" },
      ],
    });
  };

  return (
    <div className="grid lg:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
      {/* ------------------------------------------------ config rail ----- */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-4">
          {/* 1 · Data */}
          <div className="flex flex-col gap-2">
            <StepLabel n={1}>Report on</StepLabel>
            <div className="grid grid-cols-2 gap-1.5">
              {(["deals", "contacts"] as const).map((object) => (
                <button
                  key={object}
                  type="button"
                  onClick={() =>
                    update({
                      object,
                      groupBy: object === "deals" ? "stage" : "status",
                      measure: object === "deals" ? config.measure : "count",
                      filters: [],
                      chartType: undefined,
                    })
                  }
                  className={`rounded-md border px-2 py-1.5 text-sm capitalize transition-colors ${
                    config.object === object
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {object}
                </button>
              ))}
            </div>
          </div>

          {/* 2 · Measure */}
          <div className="flex flex-col gap-2">
            <StepLabel n={2}>Measure</StepLabel>
            <Select
              value={config.measure}
              onValueChange={(measure: ReportConfig["measure"]) => update({ measure })}
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Count of records</SelectItem>
                {config.object === "deals" && (
                  <>
                    <SelectItem value="sum_amount">Sum of amount</SelectItem>
                    <SelectItem value="avg_amount">Average amount</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* 3 · Group by */}
          <div className="flex flex-col gap-2">
            <StepLabel n={3}>Group by</StepLabel>
            <Select
              value={config.groupBy || "__none__"}
              onValueChange={(g) =>
                update(
                  g === "__none__"
                    ? { groupBy: "", chartType: undefined }
                    : { groupBy: g },
                )
              }
            >
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nothing - single total</SelectItem>
                {groupableFields.map((f) => (
                  <SelectItem key={f.key} value={f.key}>
                    {f.label}
                    {f.type === "date" ? " (by month)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 4 · Filters */}
          <div className="flex flex-col gap-2">
            <StepLabel n={4}>Filters</StepLabel>
            {config.filters.length > 1 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Match
                <Select
                  value={config.logic}
                  onValueChange={(logic: "all" | "any") => update({ logic })}
                >
                  <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ALL</SelectItem>
                    <SelectItem value="any">ANY</SelectItem>
                  </SelectContent>
                </Select>
                of these conditions
              </div>
            )}
            {config.filters.map((f) => {
              const field = fields.find((x) => x.key === f.field) ?? fields[0]!;
              const ops = OPS[field.type];
              return (
                <div
                  key={f.id}
                  className="rounded-md border border-border p-2 flex flex-col gap-1.5 relative"
                >
                  <button
                    type="button"
                    aria-label="Remove filter"
                    className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      update({ filters: config.filters.filter((x) => x.id !== f.id) })
                    }
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <Select
                    value={f.field}
                    onValueChange={(key) => {
                      const nf = fields.find((x) => x.key === key)!;
                      updateFilter(f.id, { field: key, op: OPS[nf.type][0]!.id, value: "", value2: "" });
                    }}
                  >
                    <SelectTrigger className="h-8 w-[calc(100%-1.5rem)] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fields.map((x) => (
                        <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1.5">
                    <Select value={f.op} onValueChange={(op) => updateFilter(f.id, { op })}>
                      <SelectTrigger className="h-8 w-28 shrink-0 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ops.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.choices ? (
                      <Select value={f.value} onValueChange={(value) => updateFilter(f.id, { value })}>
                        <SelectTrigger className="h-8 flex-1 text-sm"><SelectValue placeholder="choose…" /></SelectTrigger>
                        <SelectContent>
                          {field.choices.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="h-8 flex-1 text-sm"
                        type={field.type === "number" || f.op === "last_days" ? "number" : field.type === "date" && f.op !== "last_days" ? "date" : "text"}
                        placeholder={f.op === "last_days" ? "days" : "value"}
                        value={f.value}
                        onChange={(e) => updateFilter(f.id, { value: e.target.value })}
                      />
                    )}
                  </div>
                  {f.op === "between" && (
                    <Input
                      className="h-8 text-sm"
                      type="date"
                      value={f.value2 ?? ""}
                      onChange={(e) => updateFilter(f.id, { value2: e.target.value })}
                    />
                  )}
                </div>
              );
            })}
            <Button variant="outline" size="sm" className="self-start" onClick={addFilter}>
              <Plus className="w-4 h-4" /> Add filter
            </Button>
          </div>

          {/* 5 · Chart type */}
          <div className="flex flex-col gap-2">
            <StepLabel n={5}>Chart type</StepLabel>
            <ChartTypeGallery
              active={activeChart}
              hasGroup={!!config.groupBy}
              onPick={(chartType) => update({ chartType })}
            />
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------ live preview ---- */}
      <div className="flex flex-col gap-4 min-w-0">
        <Card>
          <CardHeader className="px-4 pb-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground tabular-nums">
                {result.rows.length} matching record{result.rows.length === 1 ? "" : "s"} ·
                total {fmt(result.total ?? 0)}
              </p>
              <p className="text-xs text-muted-foreground ml-auto">
                Saving reports is temporarily unavailable - build and view one here, then screenshot or share the numbers directly.
              </p>
            </div>
          </CardHeader>
          <CardContent className="px-4 pt-3">
            <ReportWidget config={config} height={380} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
